import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import DetectorController, { DeviceType } from '../../controller/DetectorController';
import moment from 'moment-timezone';

interface HotelDeviceSummaryTableProps {
  deviceList: DeviceType[];
  getPowerBoxStatus: any;
  getWifiBoxStatus: any;
  getAppErrorStatus: any;
}

const HotelDeviceSummaryTable = ({
  deviceList,
  getPowerBoxStatus,
  getWifiBoxStatus,
  getAppErrorStatus,
}: HotelDeviceSummaryTableProps) => {
  const occasionalDeviceCheckIn: DeviceType[] = [];
  const occasionalDeviceUnCheckIn: DeviceType[] = [];
  const constantDeviceOn: DeviceType[] = [];
  const constantDeviceOff: DeviceType[] = [];
  const today = new Date();
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setHours(today.getHours() - 48);

  deviceList.forEach((device) => {
    const isOccasional = device.device_power === 0 || device.device_wifi_power === 0; //한개라도 비상시
    const isConstant = device.device_power === 1 && device.device_wifi_power === 1; //둘다 상시

    if (isOccasional) {
      device.device_status === 'checkin'
        ? occasionalDeviceCheckIn.push(device)
        : occasionalDeviceUnCheckIn.push(device);
    } else if (isConstant) {
      device.normal === 1 ? constantDeviceOn.push(device) : constantDeviceOff.push(device);
    }
  });
  // 로그기록 48시간 이상 없는경우
  const filteredDeviceList = deviceList.filter((device) => {
    const loggedAtDate = new Date(device.logged_at);
    return loggedAtDate < twoDaysAgo;
  });
  // 기기오류+로그기록 48시간 이상 중복 제거
  const combinedDeviceList = [
    ...constantDeviceOff,
    ...filteredDeviceList.filter(
      (device) =>
        !constantDeviceOff.some((offDevice) => offDevice.user_token_id === device.user_token_id),
    ),
  ];

  const RoomStatusSection = ({
    title,
    totalCount,
    roomTypes,
  }: {
    title: string;
    totalCount: number;
    roomTypes: any[];
  }) => (
    <div>
      <h2>{title}</h2>
      <div className="totalTableWrap">
        <div className="totalRommCount">
          <h3>객실수</h3>
          <p>{totalCount}</p>
        </div>
        <div className="roomTypeWrap">
          {roomTypes.map(({ label, count, names }) => (
            <div className={`roomTypeBox ${label === '끊김' && 'error'}`} key={label}>
              <div className="roomTypeCount">
                <h3>{label}</h3>
                <p>{count}</p>
              </div>
              <div className="roomTypeList">
                <h4>{names.length ? names.join(', ') : ''}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  const sections = [
    {
      title: '전원 또는 와이파이 비상시 설정 객실',
      totalCount: occasionalDeviceCheckIn.length + occasionalDeviceUnCheckIn.length,
      roomTypes: [
        {
          label: '입실수',
          count: occasionalDeviceCheckIn.length,
          names: occasionalDeviceCheckIn.map((d) => d.name),
        },
        {
          label: '공실수',
          count: occasionalDeviceUnCheckIn.length,
          names: occasionalDeviceUnCheckIn.map((d) => d.name),
        },
      ],
    },
    {
      title: '전원과 와이파이 모두 상시 설정 객실',
      totalCount: constantDeviceOn.length + constantDeviceOff.length,
      roomTypes: [
        {
          label: '작동중',
          count: constantDeviceOn.length,
          names: constantDeviceOn.map((d) => d.name),
        },
        {
          label: '끊김',
          count: constantDeviceOff.length,
          names: constantDeviceOff.map((d) => d.name),
        },
      ],
    },
  ].filter((section) => section.totalCount > 0);
  const totalErrorDevice = deviceList
    .flatMap((subArray: any) => subArray)
    .flatMap((item) => item)
    .filter(
      (device) =>
        // 수정필요
        // !(
        //   device.device_wifi_power === 1 &&
        //   device.device_power === 0 &&
        //   device.device_is_charging === 1 &&
        //   device.loggedin_at < device.loggedout_at
        // ) &&
        device.normal === 0,
    );

  return (
    <>
      <Style>
        <div className="notice">
          <h3>* 비정상 조건</h3>
          <table className="errorTable">
            <thead>
              <tr>
                <th>전원 상태</th>
                <th>와이파이 상태</th>
                <th>조건</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>상시</td>
                <td>상시 또는 비상시</td>
                <td>충전 OFF</td>
              </tr>
              <tr>
                <td>비상시</td>
                <td>비상시</td>
                <td>와이파이 ON + 충전 OFF</td>
              </tr>
              <tr>
                <td>상시</td>
                <td>상시</td>
                <td>와이파이 OFF</td>
              </tr>
              <tr>
                <td>비상시</td>
                <td>상시</td>
                <td>전원 ON + 와이파이 OFF</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="sectionsWrap">
          {sections.map((section) => (
            <RoomStatusSection key={section.title} {...section} />
          ))}
        </div>
        {combinedDeviceList.length > 0 && (
          <div className="errorDeviceWrap">
            <div className="header">객실 기기 확인 필요 {combinedDeviceList.length}개</div>
            <div className="errorDeviceListWrap">
              {combinedDeviceList?.map((data: DeviceType, index) => (
                <div className="errorDeviceList" key={index}>
                  <h3 className="errorRoomName">{data.name}</h3>
                  <div className="errorList">
                    <div
                      style={{
                        marginBottom: '6px',
                      }}
                      className="rowCenter">
                      <div className={`powerBox ${getPowerBoxStatus(data)}`}>
                        <img
                          src={`${process.env.PUBLIC_URL}/svg/ic_power_${getPowerBoxStatus(
                            data,
                          )}.svg`}
                          alt="전원"
                          width={16}
                        />
                        <h4>{data.device_power === 0 ? '비상시' : '상시'}</h4>
                      </div>
                      <div className={`powerBox ${getWifiBoxStatus(data)}`}>
                        <img
                          src={`${process.env.PUBLIC_URL}/svg/ic_wifi_${getWifiBoxStatus(
                            data,
                          )}.svg`}
                          alt="와이파이"
                          width={16}
                        />
                        <h4>{data.device_wifi_power === 0 ? '비상시' : '상시'}</h4>
                      </div>
                    </div>
                    {new Date(data.logged_at) < twoDaysAgo && (
                      <h4 style={{ fontWeight: '700', color: 'orange' }}>48시간 사용기록 없음 </h4>
                    )}
                    {getPowerBoxStatus(data) === 'error' && (
                      <div className="rowCenter">
                        <img
                          src={`${process.env.PUBLIC_URL}/svg/ic_power_${getPowerBoxStatus(
                            data,
                          )}.svg`}
                          alt="전원"
                        />
                        <h3>충전 불량</h3>
                      </div>
                    )}

                    {getWifiBoxStatus(data) === 'error' && (
                      <div className="rowCenter start">
                        <img
                          src={`${process.env.PUBLIC_URL}/svg/ic_wifi_${getWifiBoxStatus(
                            data,
                          )}.svg`}
                          alt="와이파이"
                        />
                        <div>
                          <h3>인터넷 불량</h3>
                          <h3>{`${moment(data.loggedout_at).format('YYYY/MM/DD')}${moment(
                            data.loggedout_at,
                          ).format('HH:mm:ss')}`}</h3>
                        </div>
                      </div>
                    )}
                    {getAppErrorStatus(data) === 'error' && (
                      <div className="rowCenter">
                        <h3 className="errorTime">앱이 종료되었음</h3>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Style>
    </>
  );
};

export default HotelDeviceSummaryTable;

const Style = styled.div`
  .sectionsWrap {
    h2 {
      margin-bottom: 4px;
      font-size: 16px;
      font-weight: 500;
      color: #000;
    }
    h3 {
      color: #333;
      font-size: 14px;
    }
    h4 {
      font-weight: 400;
      font-size: 14px;
    }
    p {
      margin: 0;
      color: #333;
      font-size: 16px;
      font-weight: 600;
    }
  }

  .errorTitle {
    display: flex;

    button {
      display: flex;
      align-items: center;
      margin-left: auto;
      border-radius: 4px;
      color: #fff;
      font-size: 14px;

      img {
        transform: rotate(270deg);
        width: 24px;
      }
    }
  }
  .errorTitle.close {
    border-radius: 5px;
    button img {
      transform: rotate(90deg);
    }
  }

  .sectionsWrap {
    margin-top: 20px;
    display: flex;
    gap: 1%;
  }
  .sectionsWrap > div {
    flex: 1;
    max-width: 50%;
  }
  .totalTableWrap {
    display: flex;
    justify-content: stretch;
    align-items: center;
    border: 1px solid #e2e2e2;
    border-radius: 10px;
    background: #fff;
  }
  .totalRommCount {
    width: 15%;
    text-align: center;
    p {
      margin-top: 4px;
    }
  }
  .roomTypeWrap {
    flex: 1;
    border: 1px solid #e2e2e2;
    margin: -1px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  .roomTypeBox {
    display: flex;
  }
  .roomTypeBox:nth-of-type(1) {
    border-bottom: 1px solid #e2e2e2;
  }
  .roomTypeBox.error {
    h3,
    p {
      color: #ff6060;
      font-weight: 700;
    }
  }
  .roomTypeCount {
    width: 15%;
    padding: 6px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #e2e2e2;
    text-align: center;
    gap: 6px;
  }

  .roomTypeList {
    flex: 1;
    padding: 10px;
    word-break: keep-all;
    line-height: 24px;
  }
  .noticeBox {
    margin: 6px 0 15px 0;
    padding: 6px 12px;
    background: #e2e2e2;
    border-radius: 5px;
  }
  @media (max-width: 1300px) {
    .sectionsWrap {
      flex-direction: column;
      h2 {
        margin-top: 10px;
      }
    }
    .sectionsWrap > div {
      width: 100%;
      max-width: 100%;
    }
  }
`;
